import { SEAT_DETAIL_KEYS, seatArea, seatLabel, type EventAttendee } from '@ni/shared';
import {
  EVENT_SEATING_HEAD,
  EVENT_SEATING_NOTE,
  EVENT_SEATING_ROWLESS,
  eventSeatingConclusion,
  eventSeatingPartial,
} from '../../lib/copy';

/** One attendee as the seating reads them: who, where the list seats them, and their group. */
export interface Seated {
  id: string;
  name: string;
  identifiers: Readonly<Record<string, string>>;
  /** The attendee's group on the frame, or null when not assessed. */
  tier: string | null;
}

export interface SeatRow {
  /** "Row 4", or null for a section the list gives no rows in. */
  row: string | null;
  seats: { who: Seated; seat: string | null }[];
}

export interface SeatSection {
  /** "Section B" or "Table 12"; null for rows the list gives no section or table. */
  section: string | null;
  count: number;
  rows: SeatRow[];
}

const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

const value = (identifiers: Readonly<Record<string, string>>, key: string): string | null =>
  identifiers[key]?.trim() || null;

/** A part as `seatArea` words it ("Section B", "Table 12", "Row 4"). */
function partText(key: string, raw: string): string {
  return seatArea({ [key]: raw }) ?? raw;
}

/**
 * **The seating grouping** (docs/notes/2026-09-29-event-seating-spec.md;
 * the blend spec's "Seating"): attendees by section (or table), then row,
 * each with their seat number, from the list's own seat columns. A seat the
 * list wrote only as one whole cell is not read apart, so it has no place
 * here. The list gives no venue layout, so this is a grouping, not a map.
 */
export function seatingGroups(attendees: readonly Seated[]): SeatSection[] {
  const sections = new Map<
    string,
    { raw: string | null; key: string | null; rows: Map<string, SeatRow & { raw: string | null }> }
  >();
  for (const who of attendees) {
    const section = value(who.identifiers, SEAT_DETAIL_KEYS.section);
    const table = value(who.identifiers, SEAT_DETAIL_KEYS.table);
    const row = value(who.identifiers, SEAT_DETAIL_KEYS.row);
    if (!section && !table && !row) continue;
    const [sectionKey, sectionRaw] = section
      ? [SEAT_DETAIL_KEYS.section, section]
      : table
        ? [SEAT_DETAIL_KEYS.table, table]
        : [null, null];
    const id = `${sectionKey ?? ''}:${sectionRaw ?? ''}`;
    let group = sections.get(id);
    if (!group) {
      group = { raw: sectionRaw, key: sectionKey, rows: new Map() };
      sections.set(id, group);
    }
    const rowId = row ?? '';
    let seatRow = group.rows.get(rowId);
    if (!seatRow) {
      seatRow = { raw: row, row: row ? partText(SEAT_DETAIL_KEYS.row, row) : null, seats: [] };
      group.rows.set(rowId, seatRow);
    }
    seatRow.seats.push({ who, seat: value(who.identifiers, SEAT_DETAIL_KEYS.seat) });
  }
  return [...sections.values()]
    .sort((a, b) => (a.raw === null ? 1 : b.raw === null ? -1 : natural(a.raw, b.raw)))
    .map((group) => {
      const rows = [...group.rows.values()]
        .sort((a, b) => (a.raw === null ? 1 : b.raw === null ? -1 : natural(a.raw, b.raw)))
        .map(({ row, seats }) => ({
          row,
          seats: [...seats].sort(
            (a, b) => natural(a.seat ?? '', b.seat ?? '') || natural(a.who.name, b.who.name),
          ),
        }));
      return {
        section: group.key && group.raw ? partText(group.key, group.raw) : null,
        count: rows.reduce((sum, one) => sum + one.seats.length, 0),
        rows,
      };
    });
}

/**
 * **Who shares a row with each raised attendee**: the people the list seats in
 * the same section, table and row (`seatArea`), never inferred from seats whose
 * area differs or is missing.
 */
export function seatShares(
  attendees: readonly Seated[],
  raised: (who: Seated) => boolean,
  tierLabel: (tier: string | null) => string,
): { name: string; area: string; others: { name: string; seat: string | null; tier: string }[] }[] {
  return attendees.flatMap((who) => {
    if (!raised(who)) return [];
    const area = seatArea(who.identifiers);
    if (!area) return [];
    const others = attendees
      .filter((other) => other.id !== who.id && seatArea(other.identifiers) === area)
      .map((other) => {
        const number = value(other.identifiers, SEAT_DETAIL_KEYS.seat);
        return {
          name: other.name,
          seat: number ? seatLabel({ [SEAT_DETAIL_KEYS.seat]: number }) : null,
          tier: tierLabel(other.tier),
        };
      });
    return [{ name: who.name, area, others }];
  });
}

export function seatedOf(rows: readonly EventAttendee[], frame: string): Seated[] {
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    identifiers: row.identifiers,
    tier: row.frames.find((one) => one.frame === frame)?.tier ?? null,
  }));
}

/**
 * **Where the list seats them**: the one-line conclusion, then the grouping,
 * each seat number on its group's color.
 */
export function SeatingPlan({
  attendees,
  seated,
  raised,
  tierLabel,
  tierSwatch,
}: {
  attendees: readonly Seated[];
  /** How many on the whole list the list seats; more than shown means not all are loaded. */
  seated: number;
  raised: (who: Seated) => boolean;
  tierLabel: (tier: string | null) => string;
  tierSwatch: (tier: string | null) => string;
}) {
  const sections = seatingGroups(attendees);
  if (sections.length === 0) return null;
  const shown = sections.reduce((sum, one) => sum + one.count, 0);
  const rows = sections.reduce((sum, one) => sum + one.rows.length, 0);
  return (
    <div className="an-seating">
      <h3 className="an-factor">{EVENT_SEATING_HEAD}</h3>
      <p className="an-says">
        {eventSeatingConclusion(seatShares(attendees, raised, tierLabel), shown, rows)}
      </p>
      <div className="seat-plan">
        {sections.map((section) => (
          <div className="seat-sec" key={section.section ?? ''}>
            {section.section ? (
              <h4>
                {section.section}
                <span className="c num">{section.count}</span>
              </h4>
            ) : null}
            {section.rows.map((row) => (
              <div className="seat-row" key={row.row ?? ''}>
                <span className="rw">{row.row ?? EVENT_SEATING_ROWLESS}</span>
                <ul>
                  {row.seats.map(({ who, seat }) => (
                    <li key={who.id}>
                      <span className={`seat ${tierSwatch(who.tier)}`} title={tierLabel(who.tier)}>
                        {seat ?? '·'}
                      </span>
                      <span className="person">{who.name}</span>
                      {raised(who) ? (
                        <span className="seat-flag">{tierLabel(who.tier)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="seat-note">
        {shown < seated ? `${eventSeatingPartial(shown, seated)} ` : ''}
        {EVENT_SEATING_NOTE}
      </p>
    </div>
  );
}
